import { Box, Drawer, Flex, Portal, Text } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaPlus } from 'react-icons/fa'

import { CloseButton } from '@/components/ui/close-button'
import { useColorModeValue } from '@/components/ui/color-mode'
import { useMobileBottomNav } from '@/hooks/useMobileBottomNav'
import { useTracking } from '@/hooks/useTracking'
import { PoolingRoutes } from '@/routes/_hooks/routes.enum'
import { Link, useLocation } from '@/routing'

import { MobileBottomNavItem } from './MobileBottomNavItem'
import { UserMenu } from './UserMenu'

export const MobileBottomNav = () => {
  const { t } = useTranslation()
  const { captureEvent } = useTracking()
  const [menuOpen, setMenuOpen] = useState(false)
  const items = useMobileBottomNav()
  const { pathname } = useLocation()

  const bg = useColorModeValue('white', 'gray.900')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const fabLabelColor = useColorModeValue('brand.600', 'brand.300')

  // Order: Accueil · Recherche · [FAB] · Réservations · Menu
  const [home, search, reservations, menu] = items

  const renderTab = (item: (typeof items)[number]) => {
    const isActive = item.isMenu
      ? menuOpen
      : (item.match?.(pathname) ?? item.to === pathname)
    return (
      <MobileBottomNavItem
        key={item.key}
        item={item}
        isActive={isActive}
        onMenuClick={item.isMenu ? () => setMenuOpen(true) : undefined}
      />
    )
  }

  return (
    <>
      <Box
        display={{ base: 'block', md: 'none' }}
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        zIndex={1000}
        bg={bg}
        borderTopWidth="2px"
        borderTopColor={borderColor}
        pb="env(safe-area-inset-bottom)"
      >
        <Flex
          justifyContent="space-around"
          alignItems="flex-end"
          px={2}
          pt={2}
          pb="10px"
        >
          {renderTab(home)}
          {renderTab(search)}

          {/* Central raised "Ajouter" FAB */}
          <Flex flex={1} justifyContent="center" position="relative">
            <Link
              to={PoolingRoutes.New}
              aria-label={t('layout.mobileBottomNav.addMaterial')}
              onClick={() =>
                captureEvent('navigation_add_material_clicked', {
                  destination: PoolingRoutes.New,
                  user_authenticated: true,
                  source: 'mobile_bottom_nav_fab',
                })
              }
              style={{ textDecoration: 'none' }}
            >
              <Box
                position="absolute"
                top="-26px"
                left="50%"
                transform="translateX(-50%)"
                w="60px"
                h="60px"
                borderRadius="full"
                bg="brand.solid"
                color="brand.contrast"
                border="3px solid"
                borderColor={bg}
                boxShadow="0 10px 22px -6px rgba(201,154,46,0.6)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _active={{ transform: 'translateX(-50%) scale(0.95)' }}
              >
                <FaPlus size={28} />
              </Box>
              <Text
                fontSize="11px"
                fontWeight="semibold"
                color={fabLabelColor}
                letterSpacing="-0.01em"
                mt="37px"
                textAlign="center"
              >
                {t('layout.mobileBottomNav.addMaterial')}
              </Text>
            </Link>
          </Flex>

          {renderTab(reservations)}
          {renderTab(menu)}
        </Flex>
      </Box>

      <Drawer.Root
        open={menuOpen}
        onOpenChange={({ open }) => setMenuOpen(open)}
        placement="end"
        size="md"
      >
        <Portal>
          <Drawer.Backdrop _closed={{ pointerEvents: 'none' }} />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Header>
                <Drawer.Title flex={1}>
                  {t('layout.mobileBottomNav.menu')}
                </Drawer.Title>
                <Drawer.CloseTrigger asChild>
                  <CloseButton size="sm" colorPalette="gray" />
                </Drawer.CloseTrigger>
              </Drawer.Header>
              <Drawer.Body overflowY="auto">
                {menuOpen ? (
                  <UserMenu
                    variant="drawer"
                    onClose={() => setMenuOpen(false)}
                  />
                ) : null}
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </>
  )
}
